<template>
  <v-dialog v-model="showInsightsModal" attach="#admin-projects-container" width="600" persistent>
    <v-card>
      <v-sheet class="px-6 d-flex justify-space-between align-center" color="bg1">
        <v-card-title class="py-3 px-0">
          <v-icon class="mr-2">mdi-wrench-cog</v-icon>
          <span>Corrosion Navigator</span>
        </v-card-title>

        <!-- <v-btn :disabled="isRunning" text icon @click="showInsightsModal = false">
          <v-icon>mdi-close</v-icon>
        </v-btn> -->
      </v-sheet>

      <v-tabs v-if="debugMode" dense v-model="tab" background-color="transparent" color="primary">
        <v-tab dense v-for="(tab, index) in tabs" :tab-value="tab" :key="index">
          {{ tab }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item key="Data" value="Data">
          <v-card-text>
            <div v-if="currentImage">
              <v-sheet>
                <v-sheet class="d-flex align-center justify-space-between" style="gap: 8px">
                  <v-btn icon @click="prevImage" :disabled="currentIndex === 0">
                    <v-icon color="primary">mdi-chevron-left</v-icon>
                  </v-btn>
                  <div class="image-container">
                    <img :src="currentImage.dualloImageUrl" alt="Image" style="width: 100%" />
                  </div>
                  <v-btn
                    icon
                    @click="nextImage"
                    :disabled="currentIndex >= imagesWithInsights.length - 1">
                    <v-icon color="primary">mdi-chevron-right</v-icon>
                  </v-btn>
                </v-sheet>

                <v-sheet class="ml-10">
                  <h3 class="primary--text">
                    Current Image
                    <v-chip label small color="primary">
                      {{ `${currentIndex + 1} / ${imagesWithInsights.length}` }}
                    </v-chip>
                  </h3>
                  <p v-if="currentImage">
                    <strong>Name:</strong> {{ currentImage.name }}<br />
                    <strong>Insight Count:</strong> {{ currentImage.anomalyInsights.length }}
                    <br />
                    <strong>Location:</strong>
                    <span v-if="currentCameraPosition">
                      ({{ currentCameraPosition.x }}, {{ currentCameraPosition.y }}, {{ currentCameraPosition.z }})
                    </span>
                    <span v-else>Loading...</span>
                  </p>
                </v-sheet>
              </v-sheet>

              <v-sheet class="d-flex align-center justify-space-between" style="gap: 8px">
                <v-btn icon @click="prevInsight" :disabled="currentInsightIndex === 0">
                  <v-icon color="warning">mdi-chevron-double-left</v-icon>
                </v-btn>
                <div class="image-container">
                  <img
                    v-if="currentInsight && currentInsight.tiledImageUriPath"
                    :src="currentInsight.tiledImageUriPath"
                    alt="Image"
                    style="width: 100%; height: 200px" />
                  <v-sheet v-else color="grey lighten-2" style="width: 100%; height: 200px">
                    <v-icon class="grey--text" size="100">mdi-image-off</v-icon>
                  </v-sheet>
                </div>
                <v-btn
                  icon
                  @click="nextInsight"
                  :disabled="currentImage && currentInsightIndex >= currentImage.anomalyInsights.length - 1"
                  >
                  <v-icon color="warning">mdi-chevron-double-right</v-icon>
                </v-btn>
              </v-sheet>

              <v-sheet class="ml-10">
                <h3 class="warning--text">
                  Current Insight
                  <v-chip label small color="warning">
                    {{ `${currentInsightIndex + 1} / ${(currentImage && currentImage.anomalyInsights.length) || 0}` }}
                  </v-chip>
                </h3>
                <span v-if="currentInsight" class="mb-0">
                  <strong>ID:</strong> {{ currentInsight.friendlyId }}<br />
                  <strong>Coordinates:</strong> ({{ currentInsight.imageXCenterCoordinate }},
                  {{ currentInsight.imageYCenterCoordinate }}) <br />
                  <p v-if="currentInsight.point3d">
                    <strong>3D Target:</strong>({{ currentInsight.point3d.x }},
                    {{ currentInsight.point3d.y }}, {{ currentInsight.point3d.z }}) <br />
                  </p>
                </span>
                <p v-else>No insights available for this image.</p>
              </v-sheet>

              <!-- Debug Legend (only in debug mode) -->
              <v-sheet v-if="debugMode" class="debug-legend mt-4 pa-3" color="grey darken-4" rounded>
                <div class="caption font-weight-bold mb-2 white--text">3D Debug Legend:</div>
                <div class="d-flex flex-wrap" style="gap: 12px">
                  <span class="d-flex align-center">
                    <span class="legend-dot" style="background: #ff0000"></span>
                    <span class="caption ml-1 white--text">Center Ray</span>
                  </span>
                  <span class="d-flex align-center">
                    <span class="legend-dot" style="background: #00ff00"></span>
                    <span class="caption ml-1 white--text">Mask Polygon</span>
                  </span>
                  <span class="d-flex align-center">
                    <span class="legend-dot" style="background: #ffff00"></span>
                    <span class="caption ml-1 white--text">Mask Centroid</span>
                  </span>
                  <span class="d-flex align-center">
                    <span class="legend-dot" style="background: #0000ff"></span>
                    <span class="caption ml-1 white--text">Intersection</span>
                  </span>
                </div>
              </v-sheet>
            </div>
            <div v-else>
              <p>No images or insights available.</p>
            </div>
          </v-card-text>
        </v-tab-item>
        <v-tab-item value="Settings">
          <v-card-text>
            <v-sheet color="transparent" class="mb-4">
              <v-progress-linear
                class="rounded-pill"
                :class="{ 'animate': isRunning }"
                striped
                stream
                height="20"
                :value="processingProgress"
                color="primary">
                <template v-slot:default="{ value }">
                  <strong v-if="value < 100" class="white--text">{{ Math.ceil(value) }}%</strong>
                  <strong v-else class="white--text">Complete!</strong>
                </template>
              </v-progress-linear>
              <span v-if="processingProgress < 100" class="caption">Remaining time: {{ remainingTime }}</span>
            </v-sheet>

            <v-sheet color="transparent">
              <v-text-field
                dense
                label="Radius"
                outlined
                type="number"
                append-icon="mdi-radius-outline"
                v-model="tempRadius">
              </v-text-field>
            </v-sheet>
            <v-text-field
              :disabled="isRunning"
              dense
              label="Merge Tolerance"
              outlined
              type="number"
              append-icon="mdi-map-marker-distance"
              v-model="tempTolerance">
            </v-text-field>
            <v-sheet color="transparent">
              <v-sheet color="transparent" class="d-flex align-center justify-space-between">
                <strong>Navigation period: 1x</strong>
                <v-slider
                  hide-details
                  dense
                  v-model="timeout"
                  :min="1"
                  :max="60"
                  thumb-label
                  thumb-size="24" />
                <strong>60x</strong>
              </v-sheet>
            </v-sheet>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <v-card-actions style="background-color: var(--v-bg1-base)">
        <!-- Close btn -->
        <v-btn :disabled="isRunning || consolidateRunning" text color="error" @click="showInsightsModal = false">
          Close
        </v-btn>

        <v-spacer></v-spacer>

        <div v-if="debugMode">
          <v-tooltip top v-if="!isRunning && pc">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click="startProcessOne">
                <v-icon color="primary">mdi-wrench-cog</v-icon>
              </v-btn>
            </template>
            <span>Process One</span>
          </v-tooltip>

          <v-tooltip top v-if="pc">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click="togglePc">
                <v-icon color="blue">mdi-camera</v-icon>
              </v-btn>
            </template>
            <span>Toggle Point Cloud</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click="toggleModel">
                <v-icon color="warning">mdi-cube</v-icon>
              </v-btn>
            </template>
            <span>Toggle Model</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon v-on="on" @click="removeLines">
                <v-icon color="error">mdi-trash-can</v-icon>
              </v-btn>
            </template>
            <span>Remove Lines</span>
          </v-tooltip>
        </div>

        <!-- Actions -->
        <v-btn small :disabled="consolidateRunning" v-if="!isRunning" solo @click="delayedStartProcess" color="primary">
          <v-icon small class="mr-1">mdi-play-circle</v-icon>
          <span>Start</span>
        </v-btn>

        <v-btn small v-if="isRunning" dense @click="isRunning = false" color="warning">
          <v-icon class="mr-1">mdi-pause</v-icon>
          <span small>Stop</span>
        </v-btn>

        <v-btn
          small
          :disabled="isRunning"
          :loading="consolidateRunning"
          @click="consolidateInsights"
          color="primary"
          class="ml-4">
          <v-icon small class="mr-1">mdi-merge</v-icon>
          <span>Consolidate</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  const THREE = window.THREE;

  import { pixelToYawPitch } from '@/features/Dashboard/utils/panorama';
  import { addToScene } from '@/utils/forgeUtils';
  import { drawBoxLines, drawLine } from '@/utils/threejsUtils';
  import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

  export default {
    name: 'PostProcessDialog',
    data() {
      return {
        tabs: ['Data', 'Settings'],
        tab: 'Data',
        isRunning: false,
        consolidateRunning: false,
        timeout: 30,
        radius: 5,
        tempRadius: 5,
        radiusChangeTimeout: null,
        tempTolerance: 0.2,
        tolerance: 0.2,
        toleranceChangeTimeout: null,
        showInsightsModal: false,
        imagesWithInsights: [],
        currentCameraPosition: null,
        currentIndex: 0,
        currentInsightIndex: 0,
        potree: null,
        pc: null,
        projectPc: null,        // Fallback project point cloud
        currentLoadedPc: null,  // Currently loaded point cloud reference
      };
    },
    computed: {
      debugMode() {
        return window.location.hostname.includes('localhost');
      },
      viewer() {
        return this.$store.state.viewer;
      },
      forgeService() {
        return this.$store.state.forgeService;
      },
      images360() {
        return this.$store.getters['getImages'];
      },
      currentImage() {
        return this.imagesWithInsights[this.currentIndex] || null;
      },
      currentInsight() {
        return this.currentImage?.anomalyInsights[this.currentInsightIndex] || null;
      },
      pointCloudSettings() {
        return this.$store.state.userSettings.displaySettings.pointCloudDisplay;
      },
      insightsCount() {
        return this.imagesWithInsights.reduce(
          (acc, image) => acc + image.anomalyInsights.length,
          0,
        );
      },
      processedInsightsCount() {
        return this.imagesWithInsights.reduce(
          (acc, image) => acc + image.anomalyInsights.filter((insight) => insight.point3d).length,
          0,
        );
      },
      processingProgress() {
        return this.insightsCount ? (this.processedInsightsCount / this.insightsCount) * 100 : 0;
      },
      remainingTime() {
        let seconds = this.timeout * (this.insightsCount - this.processedInsightsCount);
        let hours = Math.floor(seconds / 3600);
        let minutes = Math.ceil((seconds % 3600) / 60);

        if (hours === 0 && minutes <= 1) {
          return 'Less than a minute';
        }

        return `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}`;
      },
    },
    methods: {
      async openInsightsModal(imagesWithInsights, pc) {
        this.isRunning = false;

        if (!imagesWithInsights.length) {
          this.$dialog.notify.error('No images with insights found', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        this.pc = pc;
        this.projectPc = pc;        // Store as fallback project PC
        this.currentLoadedPc = null; // Reset PC tracking

        this.imagesWithInsights = imagesWithInsights;
        this.currentIndex = 0;
        this.currentInsightIndex = 0;
        this.showInsightsModal = true;

        this.navigateToCurrentImage();
      },
      async prevImage() {
        if (this.currentIndex > 0) {
          this.currentIndex--;
          this.currentInsightIndex = 0;
          await this.navigateToCurrentImage(); // Load PC for new image
        }
      },
      async nextImage() {
        if (this.currentIndex < this.imagesWithInsights.length - 1) {
          this.currentIndex++;
          this.currentInsightIndex = 0;
          await this.navigateToCurrentImage(); // Load PC for new image
        }
      },
      prevInsight() {
        if (this.currentInsightIndex > 0) {
          this.currentInsightIndex--;
          this.navigateToCurrentImage();
        }
      },
      nextInsight() {
        if (this.currentInsightIndex < this.currentImage.anomalyInsights.length - 1) {
          this.currentInsightIndex++;
          this.navigateToCurrentImage();
        }
      },
      /**
       * Load the appropriate point cloud for the given image.
       * Uses image's own point cloud if available, otherwise falls back to project PC.
       * Hides all other point clouds before loading.
       * @param {Object} image - The Image360 object
       * @returns {Promise<Object|null>} - The loaded potree instance or null
       */
      async loadPointCloudForImage(image) {
        if (!image) return null;

        // Get image's own point cloud or fallback to project PC
        const imagePC = image.scanPointClouds;
        const targetPc = imagePC || this.projectPc;

        if (!targetPc) {
          debugWarn('No point cloud available for image:', image.name);
          return null;
        }

        // Skip if already loaded (same PC)
        if (this.currentLoadedPc && this.currentLoadedPc.id === targetPc.id) {
          return this.potree;
        }

        if (this.debugMode) {
          debugLog('%c Loading PC for image:', 'color: blue; font-weight: bold;', {
            imageName: image.name,
            pcName: targetPc.name,
            isImagePC: !!imagePC,
            isFallback: !imagePC
          });
        }

        // Hide all point clouds first
        this.forgeService.getMainExtension()?.hideAllPointClouds();

        // Load the target point cloud
        this.currentLoadedPc = targetPc;
        this.pc = targetPc; // Update legacy reference

        const pc = await this.forgeService.setShowingPointCloudIn3D(targetPc, true);
        this.potree = pc;

        // Apply clipping box
        this.clipImagePc();

        return pc;
      },

      async navigateToCurrentImage() {
        const image = this.currentImage;

        if (!image) return;

        // Load appropriate point cloud for this image (handles PC switching)
        await this.loadPointCloudForImage(image);

        if (this.potree) {
          this.clipImagePc();
        }

        this.currentCameraPosition = image.getCameraPositionInViewer();

        this.forgeService.navigateIn3D(image);

        this.navigateToCurrentInsight();
      },
      navigateToCurrentInsight() {
        const viewer = this.$store.state.viewer;
        const insight = this.currentInsight;

        if (!insight) return;

        const { width, height } = this.currentImage;

        const { imageXCenterCoordinate, imageYCenterCoordinate } = insight;

        const center = pixelToYawPitch(
          imageXCenterCoordinate,
          imageYCenterCoordinate,
          width ?? 4057,
          height ?? 2028,
        );

        const target = this.forgeService.imageYawPitchToPoint3D(
          this.currentImage,
          center.yaw,
          center.pitch,
        );

        insight.target = target;

        viewer.navigation.setTarget(target);
      },
      removeLines() {
        this.viewer.impl.removeOverlayScene('test-overlay');
      },
      startProcessOne() {
        this.processInsight();
      },
      async startProcess() {
        for (let i = 0; i < this.imagesWithInsights.length; i++) {
          if (!this.isRunning) break;

          let processedInsights = [];
          let toBeDeleted = [];

          this.currentIndex = i;
          await this.navigateToCurrentImage(); // Wait for PC to load for this image

          this.imagesWithInsights[i].anomalyInsights = this.imagesWithInsights[i].anomalyInsights.sort((a, b) => {
            const yDiff = (Number(a.imageYCenterCoordinate) || 0) - (Number(b.imageYCenterCoordinate) || 0);
            const xDiff = (Number(a.imageXCenterCoordinate) || 0) - (Number(b.imageXCenterCoordinate) || 0);
            return yDiff !== 0 ? yDiff : xDiff;
          });

          for (let j = 0; j < this.imagesWithInsights[i].anomalyInsights.length; j++) {
            if (!this.isRunning) break;

            this.currentInsightIndex = j;

            if (this.currentInsight.point3d) {
              let p = new THREE.Vector3(
                this.currentInsight.point3d.x,
                this.currentInsight.point3d.y,
                this.currentInsight.point3d.z,
              );

              if (p.distanceTo(this.currentImage.getCameraPositionInViewer()) > this.radius) {
                toBeDeleted.push(this.currentInsight.id);
              }

              continue;
            }

            this.navigateToCurrentInsight();

            await this.delayedProcessInsight(this.currentInsight)
              .then(() => {
                if (!this.currentInsight.point3d || this.currentInsight.point3d.distanceTo(this.currentImage.getCameraPositionInViewer()) > this.radius) {
                  toBeDeleted.push(this.currentInsight.id);
                } else {
                  processedInsights.push(this.currentInsight);
                }
              })
              .catch(() => {
                this.isRunning = false;
              });
          }

          if (toBeDeleted.length) {
            this.$store.dispatch('AI_INSPECT/DELETE_MULTI', toBeDeleted).then(() => {
              this.imagesWithInsights[i].anomalyInsights = this.imagesWithInsights[i].anomalyInsights.filter(
                (insight) => !toBeDeleted.includes(insight.id),
              );
            });
          }

          if (processedInsights.length) {
            debugLog('%c processedInsights', 'color: red; font-weight: bold;', processedInsights);
            debugLog('%c toBeDeleted', 'color: red; font-weight: bold;', toBeDeleted);
            this.$store.dispatch('AI_INSPECT/UPDATE_MULTIPLE', processedInsights);
          }
        }

        this.isRunning = false;
      },
      async delayedStartProcess() {
        this.isRunning = true;

        // Common initialization logic
        const initializePointCloudSettings = async () => {
          this.pointCloudSettings.pointBudget = 1e9;
          this.pointCloudSettings.pointSize = 6;
          this.pointCloudSettings.pointType = 'Square';

          // PC is now loaded per-image in loadPointCloudForImage()
          // Load initial PC for the first image
          await this.loadPointCloudForImage(this.currentImage);

          return new Promise((resolve) => {
            setTimeout(() => {
              this.startProcess();
              resolve();
            }, 30000);
          });
        };

        const reset3DPoints = () => {
          this.imagesWithInsights.forEach((image) => {
            image.anomalyInsights.forEach((insight) => {
              insight.point3d = null;
            });
          });
        };

        const has3DPoints = this.imagesWithInsights.some((image) =>
          image.anomalyInsights.some((insight) => insight.point3d),
        );

        if (has3DPoints) {
          this.$dialog.confirm({
            text: 'There are already insights with 3D points. Do you want to continue?',
            title: 'Start Again',
            actions: {
              false: {
                text: 'Continue',
                color: 'secondary',
                handle: async () => {
                  initializePointCloudSettings();
                },
              },
              true: {
                text: 'Start Again',
                color: 'primary',
                handle: async () => {
                  reset3DPoints();
                  initializePointCloudSettings();
                },
              },
            },
          });
        } else {
          initializePointCloudSettings();
        }
      },
      delayedProcessInsight(insight) {
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            if (!this.isRunning) {
              reject();
              return;
            }

            this.processInsight(insight);

            resolve();
          }, this.timeout * 1000);
        });
      },
      /**
       * Parse maskPoints JSON string to array of {x, y} objects
       * @param {string} maskPointsJson - JSON string like '["16809, 530","16824, 530"]'
       * @returns {Array<{x: number, y: number}>}
       */
      parseMaskPoints(maskPointsJson) {
        if (!maskPointsJson) return [];
        try {
          const maskPointsArray = JSON.parse(maskPointsJson);
          return maskPointsArray.map((pointStr) => {
            const [x, y] = pointStr.split(',').map(coord => parseFloat(coord.trim()));
            return { x, y };
          });
        } catch (error) {
          debugError('Error parsing maskPoints:', error, maskPointsJson);
          return [];
        }
      },

      /**
       * Calculate the 2D centroid (geometric center) of mask points
       * @param {Array<{x: number, y: number}>} points2D - Array of 2D points
       * @returns {{x: number, y: number}|null} - Centroid coordinates or null
       */
      calculate2DCentroid(points2D) {
        if (!points2D || points2D.length === 0) return null;

        const sumX = points2D.reduce((acc, p) => acc + p.x, 0);
        const sumY = points2D.reduce((acc, p) => acc + p.y, 0);

        return {
          x: sumX / points2D.length,
          y: sumY / points2D.length
        };
      },

      /**
       * Convert a single 2D pixel coordinate to 3D point via raycasting
       * @param {number} x - X pixel coordinate
       * @param {number} y - Y pixel coordinate
       * @param {Object} image - The panorama image object
       * @returns {THREE.Vector3|null} - 3D intersection point or null
       */
      convert2DPointTo3D(x, y, image) {
        const { width, height } = image;

        // Convert pixel to yaw/pitch
        const { yaw, pitch } = pixelToYawPitch(x, y, width ?? 4057, height ?? 2028);

        // Get directional target
        const target = this.forgeService.imageYawPitchToPoint3D(image, yaw, pitch);

        // Convert target to screen coordinates (same approach as img360MouseTo3DPoint)
        const viewer = this.$store.state.viewer;
        const clientPoint = viewer.worldToClient(target);

        // Try point cloud intersection using screen-space picker
        const rect = viewer.impl.canvas.getBoundingClientRect();
        const pointer = {
          x: clientPoint.x + rect.left,
          y: clientPoint.y + rect.top
        };

        const pointCloudIntersection = this.forgeService
          .getMainExtension()
          .pickNearestPointCloudPoint(pointer);

        // Return point cloud intersection if available, otherwise model intersection
        return pointCloudIntersection;
      },

      /**
       * Convert all maskPoints from 2D to 3D coordinates
       * @param {Object} insight - The insight object with maskPoints property
       * @returns {Array<{x: string, y: string, z: string}>} - Array of 3D points
       */
      convertMaskPointsTo3D(insight) {
        if (!insight.maskPoints) return [];

        const points2D = this.parseMaskPoints(insight.maskPoints);
        const points3D = [];

        for (const point2D of points2D) {
          const point3D = this.convert2DPointTo3D(point2D.x, point2D.y, this.currentImage);
          if (point3D) {
            points3D.push({
              x: point3D.x.toFixed(4),
              y: point3D.y.toFixed(4),
              z: point3D.z.toFixed(4)
            });
          }
        }

        return points3D;
      },

      processInsight(insight) {
        const imagePoint = this.currentImage.getCameraPositionInViewer();

        if (!insight) {
          insight = this.currentInsight;
        }

        const target = insight.target;

        let clientPoint = this.viewer.worldToClient(target);

        const rect = this.viewer.impl.canvas.getBoundingClientRect();
        let pointer = {
          x: clientPoint.x + rect.left,
          y: clientPoint.y + rect.top
        }

        let pointCloudIntersection = this.forgeService
          .getMainExtension()
          .pickNearestPointCloudPoint(pointer);
         
        if (pointCloudIntersection) {
          insight.point3d = pointCloudIntersection;
          if (this.debugMode) this.drawRay(imagePoint, pointCloudIntersection);
        }

        // Convert maskPoints to 3D and store in description for debugging
        if (insight.maskPoints) {
          const maskPoints3D = this.convertMaskPointsTo3D(insight);
          insight.description = JSON.stringify(maskPoints3D);

          if (this.debugMode) {
            debugLog('%c maskPoints3D', 'color: green; font-weight: bold;', maskPoints3D);
            // Draw the mask polygon in 3D for visual debugging
            this.drawMaskPolygon3D(maskPoints3D);

            // Calculate and draw 2D centroid converted to 3D
            const points2D = this.parseMaskPoints(insight.maskPoints);
            const centroid2D = this.calculate2DCentroid(points2D);
            if (centroid2D) {
              const centroid3D = this.convert2DPointTo3D(centroid2D.x, centroid2D.y, this.currentImage);
              if (centroid3D) {
                debugLog('%c centroid2D', 'color: orange; font-weight: bold;', centroid2D);
                debugLog('%c centroid3D', 'color: yellow; font-weight: bold;', centroid3D);
                this.drawCentroid3D(centroid3D);
              }
            }
          }
        }
      },
      clipImagePc() {
        if (!this.currentImage || !this.potree) return;

        var point = this.currentImage.getCameraPositionInViewer();
        
        var p1 = new THREE.Vector3(
          point.x - this.radius,
          point.y - this.radius,
          point.z - this.radius,
        );
        var p2 = new THREE.Vector3(
          point.x + this.radius,
          point.y + this.radius,
          point.z + this.radius,
        );

        var box = new THREE.Box3();

        box.setFromPoints([p1, p2]);

        if (this.debugMode) {
          drawBoxLines(box, 0xff0000, this.viewer, 'test-overlay');
        }

        this.potree.setClippingBoxNew(box);
      },
      drawRay(start, end) {
        drawLine(start, end.clone(), 0xff0000, this.viewer, 'test-overlay');

        const sphereGeometry = new THREE.SphereGeometry(0.01, 32, 32);
        const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000, depthTest: false });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.position.copy(end);
        addToScene(sphere, this.viewer, 'test-overlay');

        const pointGeometry = new THREE.SphereGeometry(0.005, 32, 32);
        const pointMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff, depthTest: false });
        const point = new THREE.Mesh(pointGeometry, pointMaterial);
        point.position.copy(end);
        addToScene(point, this.viewer, 'test-overlay');
      },
      /**
       * Draw the mask polygon in 3D for debugging
       * @param {Array<{x: string, y: string, z: string}>} points3D - Array of 3D points
       */
      drawMaskPolygon3D(points3D) {
        if (!points3D || points3D.length === 0) return;

        const color = 0x00ff00; // Green for mask polygon (different from red ray)

        // Draw spheres at each vertex
        points3D.forEach((point) => {
          const position = new THREE.Vector3(
            parseFloat(point.x),
            parseFloat(point.y),
            parseFloat(point.z)
          );

          // Sphere for each vertex
          const sphereGeometry = new THREE.SphereGeometry(0.01, 16, 16);
          const sphereMaterial = new THREE.MeshBasicMaterial({
            color: color,
            depthTest: false
          });
          const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
          sphere.position.copy(position);
          addToScene(sphere, this.viewer, 'test-overlay');
        });

        // Draw lines connecting the vertices (polygon outline)
        for (let i = 0; i < points3D.length; i++) {
          const start = new THREE.Vector3(
            parseFloat(points3D[i].x),
            parseFloat(points3D[i].y),
            parseFloat(points3D[i].z)
          );
          const end = new THREE.Vector3(
            parseFloat(points3D[(i + 1) % points3D.length].x),
            parseFloat(points3D[(i + 1) % points3D.length].y),
            parseFloat(points3D[(i + 1) % points3D.length].z)
          );

          drawLine(start, end, color, this.viewer, 'test-overlay');
        }
      },
      /**
       * Draw the centroid point in 3D for debugging
       * @param {THREE.Vector3} centroid3D - The 3D centroid position
       */
      drawCentroid3D(centroid3D) {
        if (!centroid3D) return;

        const color = 0xffff00; // Yellow for centroid

        // Centroid sphere (halved size for visibility)
        const sphereGeometry = new THREE.SphereGeometry(0.02, 32, 32);
        const sphereMaterial = new THREE.MeshBasicMaterial({
          color: color,
          depthTest: false
        });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.position.copy(centroid3D);
        addToScene(sphere, this.viewer, 'test-overlay');
      },
      togglePc() {
        this.pointCloudSettings.pointBudget = 1e7;
        this.pointCloudSettings.pointSize = 5;
        this.pointCloudSettings.pointType = 'Square';

        this.forgeService.toggleShowingPointCloudIn3D(this.pc).then((pc) => {
          this.potree = pc;
        });
      },
      toggleModel() {
        let hidden = this.viewer.getHiddenNodes();
        if (hidden.length > 0) {
          this.viewer.showAll();
        } else {
          this.viewer.hideAll();
        }
      },
      async mergeInsightsWithinImage() {
        return new Promise((resolve) => {
          this.imagesWithInsights.forEach((image) => {
            const visited = new Set();
            const toDelete = [];
  
            image.anomalyInsights.forEach((insight) => {
              if (visited.has(insight.id) || !insight.point3d) return;
  
              const group = [insight];
              visited.add(insight.id);
  
              image.anomalyInsights.forEach((otherInsight) => {
                if (visited.has(otherInsight.id) || !otherInsight.point3d) return;
  
                const distance = new THREE.Vector3(
                  insight.point3d.x - otherInsight.point3d.x,
                  insight.point3d.y - otherInsight.point3d.y,
                  insight.point3d.z - otherInsight.point3d.z,
                ).length();
  
                if (distance <= this.tolerance) {
                  group.push(otherInsight);
                  visited.add(otherInsight.id);
                }
              });
  
              if (group.length > 1) {
                const primary = group.sort((a, b) => a.id - b.id)[0];
                group.forEach((insight) => {
                  if (insight !== primary) {
                    toDelete.push(insight.id);
                  }
                });
              }
            });
  
            if (toDelete.length) {
              this.$store.dispatch('AI_INSPECT/DELETE_MULTI', toDelete).then(() => {
                image.anomalyInsights = image.anomalyInsights.filter(
                  (insight) => !toDelete.includes(insight.id),
                );
                resolve();
              }).catch(() => {
                this.$dialog.notify.error('Failed to delete duplicate insights', {
                  position: 'top-right',
                  timeout: 3000,
                });
                resolve();
              });
            } else {
              resolve();
            }
          });
        });
      },
      async consolidateInsights() {
        this.consolidateRunning = true;

        // Remove duplicate insights within the same image within the tolerance and mark as deleted
        await this.mergeInsightsWithinImage();

        const consolidatedGroups = []; // To store grouped insights
        const visited = new Set(); // To mark insights as visited
        let groupId = 1; // Counter for group IDs
        const cameraToPositionMap = new Map();

        // Group insights by 3D position with tolerance
        for (let i = 0; i < this.imagesWithInsights.length; i++) {
          const image = this.imagesWithInsights[i];
          cameraToPositionMap.set(image.id, image.getCameraPositionInViewer());
          image.anomalyInsights.forEach((insight) => {
            if (visited.has(insight.id) || !insight.point3d) return; // Skip already visited insights

            const group = [insight]; // Start a new group
            visited.add(insight.id);

            // Compare only with subsequent images
            for (let j = i + 1; j < this.imagesWithInsights.length; j++) {
              const otherImage = this.imagesWithInsights[j];

              otherImage.anomalyInsights.forEach((otherInsight) => {
                if (visited.has(otherInsight.id) || !otherInsight.point3d) return;

                const insightPosition = new THREE.Vector3(
                  insight.point3d.x,
                  insight.point3d.y,
                  insight.point3d.z,
                );
                const otherPosition = new THREE.Vector3(
                  otherInsight.point3d.x,
                  otherInsight.point3d.y,
                  otherInsight.point3d.z,
                );

                if (insightPosition.distanceTo(otherPosition) <= this.tolerance) {
                  group.push(otherInsight);
                  visited.add(otherInsight.id);
                }
              });
            }

            consolidatedGroups.push({ id: groupId++, insights: group });
          });
        }

        const summary = []; // Summary for logging

        // Process each group
        consolidatedGroups.forEach((group) => {
          // Get camera positions and distances
          const distances = group.insights.map((insight) => {
            const insightPosition = new THREE.Vector3(
              insight.point3d.x,
              insight.point3d.y,
              insight.point3d.z,
            );
            const cameraPosition = cameraToPositionMap.get(insight.imageId);
            const cameraVector = new THREE.Vector3(
              cameraPosition.x,
              cameraPosition.y,
              cameraPosition.z,
            );

            return {
              insight,
              distance: cameraVector.distanceTo(insightPosition),
            };
          });

          // Sort by distance
          distances.sort((a, b) => a.distance - b.distance);

          const primary = distances[0]; // Nearest insight as primary
          const withinThreeMeters = distances.filter((d) => d.distance <= 3);

          let primaryInsight;
          if (withinThreeMeters.length > 0) {
            // Use nearest within 3 meters as primary
            primaryInsight = withinThreeMeters[0].insight;

            // Update parentId for the rest
            group.insights.forEach((insight) => {
              if (insight !== primaryInsight) {
                insight.parentId = primaryInsight.id;
              }
            });
          } else {
            // No insights within 3 meters, use the nearest
            primaryInsight = primary.insight;

            group.insights.forEach((insight) => {
              if (insight !== primaryInsight) {
                insight.parentId = primaryInsight.id;
                insight.needToDelete = true;
              }
            });
          }

          // Prepare summary for the group
          summary.push({
            groupId: group.id,
            primaryInsightId: primaryInsight.id,
            numberOfInsights: group.insights.length,
            insightsDetails: group.insights.map((insight) => ({
              id: insight.id,
              needToDelete: insight.needToDelete || false,
            })),
          });
        });

        // Log summary as a table
        console.table(
          summary
            .filter((item) => item.insightsDetails.length > 1)
            .map((item) => ({
              GroupID: item.groupId,
              PrimaryInsightID: item.primaryInsightId,
              NumberOfInsights: item.numberOfInsights,
              Insights: item.insightsDetails.map((detail) => detail.id).join(', '),
              NeedToDelete: item.insightsDetails.some((detail) => detail.needToDelete),
            })),
        );

        // Update imagesWithInsights with consolidated insights
        var toDeleteIds = [];
        this.imagesWithInsights.forEach((image) => {
          image.anomalyInsights.forEach((insight) => {
            if (insight.needToDelete) {
              toDeleteIds.push(insight.id);
            }
          });
          image.anomalyInsights = image.anomalyInsights.filter((insight) => !insight.needToDelete);
        });

        let promises = [];

        if (toDeleteIds.length) {
          let p1 = this.$store.dispatch('AI_INSPECT/DELETE_MULTI', toDeleteIds);
          promises.push(p1);
        }

        if (consolidatedGroups.length) {
          let p2 = this.$store.dispatch(
            'AI_INSPECT/UPDATE_MULTIPLE',
            consolidatedGroups.flatMap((group) => group.insights),
          );
          promises.push(p2);
        }

        if (promises.length) {
          Promise.all(promises).then(() => {
            this.$dialog.notify.success('Insights consolidated successfully', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch(() => {
            this.$dialog.notify.error('Failed to consolidate insights', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .finally(() => {
            this.consolidateRunning = false;
          });
        }
      },
    },
    mounted() {
      this.tab = this.debugMode ? 'Data' : 'Settings';
    },
    watch: {
      tempRadius: {
        handler() {
          if (this.radiusChangeTimeout) {
            clearTimeout(this.radiusChangeTimeout);
          }

          this.radiusChangeTimeout = setTimeout(() => {
            if (this.tempRadius < 0) {
              this.tempRadius = 1;
            }
            this.radius = Number.parseFloat(this.tempRadius);
            this.clipImagePc();
          }, 500);
        },
      },
      toleranceChange: {
        handler() {
          if (this.toleranceChangeTimeout) {
            clearTimeout(this.toleranceChangeTimeout);
          }

          this.toleranceChangeTimeout = setTimeout(() => {
            if (this.tempTolerance < 0) {
              this.tempTolerance = 0.1;
            }
            this.tolerance = Number.parseFloat(this.tempTolerance);
          }, 500);
        },
      },
    },
  };
</script>
<style lang="scss" scoped>
  /* .v-dialog__content {
    position: absolute !important;
  } */

  /* Debug Legend Styles */
  .debug-legend .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
    box-shadow: 0 0 4px rgba(255, 255, 255, 0.3);
  }
</style>
